// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@keller/App/Components/Common/XngZone";
import XngSticky from "@keller/App/Components/XngSticky";
import { DivWrapper } from "@keller/XDL/constants";
import { hideElementOnPrint, xClassNames, xScreen } from "@keller/XDL/Utilities";
import React from "react";
import { css } from "styled-components";

const enum fields {
    showHeaderActionSection = "showHeaderActionSection",
    showShippingAndBillingSection = "showShippingAndBillingSection",
    showExtraInfoSection = "showExtraInfoSection",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showHeaderActionSection]: boolean;
        [fields.showShippingAndBillingSection]: boolean;
        [fields.showExtraInfoSection]: boolean;
    };
}

export interface MyAccountDetailsPagesLayoutStyles {
    layoutContainer?: InjectableCss;
    container?: InjectableCss;
    headerContainer?: InjectableCss;
    infoContainer?: InjectableCss;
    totalContainer?: InjectableCss;
    totalContainerMobile?: InjectableCss;
    infoSection?: InjectableCss;
    shippingAndBillingSection?: InjectableCss;
    extraInfoSection?: InjectableCss;
    productListSection?: InjectableCss;
    richContent?: InjectableCss;
    richContentOnPrint?: InjectableCss;
    detailsSectionContainer?: InjectableCss;
}

export const myAccountDetailsPagesLayoutStyles: MyAccountDetailsPagesLayoutStyles = {
    container: {
        css: css`
            display: flex;
            gap: 41px;
        `,
    },
    headerContainer: {
        css: css`
            display: flex;
            margin-bottom: 24px;
            &.show-header-actions {
                gap: 20px;
                justify-content: space-between;
            }
            [data-test-selector="PageTitle"] {
                margin-bottom: 0;
            }
            h1 {
                @media print {
                    font-size: 28px !important;
                }
            }
            @media print {
                [data-test-selector="PageTitle"] {
                    line-height: 18px;
                }
            }
        `,
    },
    infoContainer: {
        css: css`
            flex: 1 1 auto;
            & > div {
                margin-bottom: 24px;
                &:nth-last-child(2) {
                    margin-bottom: 36px;
                    @media print {
                        margin-bottom: 0;
                    }
                }
                &:last-child {
                    margin-bottom: 0;
                    @media print {
                        margin-top: 0;
                    }
                }
                @media print {
                    margin-bottom: 8px;
                }
            }
        `,
    },
    infoSection: {
        css: css`
            & > ${DivWrapper} {
                display: flex;
                gap: 12px 40px;
                flex-wrap: wrap;
                ${xScreen("lte", "xs")} {
                    flex-direction: column;
                    flex-wrap: nowrap;
                    gap: 15px;
                }
                @media print {
                    gap: 12px 28px;
                }
            }
            @media print {
                border-bottom: 1px solid ${getColor("common.ui5")};
                padding-bottom: 8px;
                margin-bottom: 16px;
            }
        `,
    },
    totalContainer: {
        css: css`
            flex: 0 0 288px;
            ${xScreen("lte", "sm")} {
                display: none;
            }
            @media print {
                display: none;
            }
        `,
    },
    totalContainerMobile: {
        css: css`
            ${xScreen("gt", "sm")} {
                display: none;
            }
            @media print {
                display: inline-flex;
                width: 255px;
                margin-left: 12px;
                vertical-align: top;
            }
        `,
    },
    detailsSectionContainer: {
        css: css`
            @media print {
                width: calc(100% - 267px);
                display: inline-flex;
                flex-wrap: wrap;
                flex-direction: row;
            }
        `,
    },
    richContent: {
        css: css`
            ${hideElementOnPrint}
        `,
    },
    richContentOnPrint: {
        css: css`
            display: none;
            @media print {
                display: block;
                div {
                    margin-left: 0 !important;
                }
            }
        `,
    },
    shippingAndBillingSection: {
        css: css`
            width: 100%;
            gap: 30px;
            display: flex;
            justify-content: space-between;
            ${xScreen("lte", "md")} {
                flex-wrap: wrap;
                ${DivWrapper} {
                    width: 30%;
                }
            }
            ${xScreen("lte", "xs")} {
                ${DivWrapper} {
                    width: 100%;
                }
            }
            ${DivWrapper} {
                &:empty {
                    display: none;
                }
                flex-grow: 1;
            }
            @media print {
                display: inline-flex;
                gap: 12px;
                flex-wrap: nowrap;
            }
        `,
    },
    extraInfoSection: {
        css: css`
            ${hideElementOnPrint}
        `,
    },
    productListSection: {
        css: css`
            margin-top: 30px;
        `,
    },
};

const styles = myAccountDetailsPagesLayoutStyles;

type Props = OwnProps;

const MyAccountDetailsPagesLayout = ({
    id,
    fields: { showHeaderActionSection, showExtraInfoSection, showShippingAndBillingSection },
}: Props) => {
    return (
        <StyledWrapper {...styles.layoutContainer}>
            <StyledWrapper
                {...styles.headerContainer}
                className={xClassNames.append(showHeaderActionSection ? "show-header-actions" : "")}
            >
                <XngZone contentId={id} zoneName="Content00" hint="Zone For Header" />
                {showHeaderActionSection && (
                    <XngZone contentId={id} zoneName="Content01" hint="Zone For Header actions buttons" />
                )}
            </StyledWrapper>
            <StyledWrapper {...styles.container}>
                <StyledWrapper {...styles.infoContainer}>
                    <StyledWrapper {...styles.infoSection}>
                        <XngZone contentId={id} zoneName="Content02" hint="Zone For Details Information" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.detailsSectionContainer}>
                        {showShippingAndBillingSection && (
                            <StyledWrapper {...styles.shippingAndBillingSection}>
                                <XngZone
                                    contentId={id}
                                    zoneName="Content04"
                                    hint="Zone For Details Billing Information"
                                />
                                <XngZone
                                    contentId={id}
                                    zoneName="Content10"
                                    hint="Zone For  PickUp Details Shipping Information"
                                />
                                <XngZone
                                    contentId={id}
                                    zoneName="Content03"
                                    hint="Zone For Details Shipping Information"
                                />
                            </StyledWrapper>
                        )}

                        <StyledWrapper {...styles.richContentOnPrint}>
                            <XngZone contentId={id} zoneName="Content08" hint="Zone For Rich Content" />
                        </StyledWrapper>
                    </StyledWrapper>
                    {showExtraInfoSection && (
                        <StyledWrapper {...styles.extraInfoSection}>
                            <XngZone contentId={id} zoneName="Content05" hint="Zone For Details Extra Information" />
                        </StyledWrapper>
                    )}
                    <StyledWrapper {...styles.totalContainerMobile}>
                        <XngZone contentId={id} zoneName="Content06" hint="Zone For Total Summary" />
                        <StyledWrapper {...styles.richContent}>
                            <XngZone contentId={id} zoneName="Content08" hint="Zone For Rich Content" />
                        </StyledWrapper>
                    </StyledWrapper>
                    <StyledWrapper {...styles.productListSection}>
                        <XngZone contentId={id} zoneName="Content07" hint="Zone For Line Listing" />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.totalContainer}>
                    <XngSticky stopBreakpoint="sm">
                        <XngZone contentId={id} zoneName="Content06" hint="Zone For Total Summary" />
                        <StyledWrapper {...styles.richContent}>
                            <XngZone contentId={id} zoneName="Content08" hint="Zone For Rich Content" />
                        </StyledWrapper>
                    </XngSticky>
                </StyledWrapper>
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: MyAccountDetailsPagesLayout,
    definition: {
        group: "Layout" as any,
        displayName: "My Account Details Pages Layout",
        fieldDefinitions: [
            {
                name: fields.showHeaderActionSection,
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.showShippingAndBillingSection,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
            {
                name: fields.showExtraInfoSection,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
